/**
 * `access_sessions` as a table (design 19 §7.2 "Current sessions", §7.5 item 1).
 *
 * Shared by the gateway detail's Sessions tab and by Secure Access → Activity,
 * because they ask the same question with different filters pinned.
 *
 * Filters arrive as PRIMITIVE props, not as an options object: an object
 * literal is a new identity on every parent render, which would restart the
 * fetch effect in a loop.
 *
 * The User column obeys the identity rule (brief §3 WP4): a device-only session
 * carries the certificate CN in `user`, and showing that under "User" would
 * claim a person signed in when nobody did.
 */

import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { UserCell } from './MetricCells';
import accessObservabilityService, { type AccessSessionRow, type SessionAppRollup } from '../../services/accessObservability.service';
import { fmtBytes, fmtDuration } from '../../utils/obsChart';
import { resolveTimeRange, type TimeRangeValue } from '../../utils/timeRange';

interface SessionsTableProps {
  range: TimeRangeValue;
  gatewayId?: string;
  deviceCn?: string;
  user?: string;
  /** Show the gateway/node column (hidden when the page already is one node). */
  showNode?: boolean;
  /** Initial state of the open-only toggle. */
  defaultOpenOnly?: boolean;
  limit?: number;
  onSelect?: (session: AccessSessionRow) => void;
  selectedId?: string | null;
  title?: string;
}

export const SessionsTable = ({
  range,
  gatewayId,
  deviceCn,
  user,
  showNode = true,
  defaultOpenOnly = false,
  limit = 100,
  onSelect,
  selectedId = null,
  title = 'Sessions',
}: SessionsTableProps) => {
  const [rows, setRows] = useState<AccessSessionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openOnly, setOpenOnly] = useState(defaultOpenOnly);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { fromIso, toIso } = resolveTimeRange(range);
      const res = await accessObservabilityService.listSessions(
        {
          gatewayId,
          deviceCn,
          user,
          from: fromIso,
          to: toIso,
          openOnly: openOnly || undefined,
          limit,
        },
        { showLoading: false },
      );
      setRows(res?.sessions ?? []);
      setTotal(res?.total ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sessions');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [range, gatewayId, deviceCn, user, openOnly, limit]);

  useEffect(() => {
    void load();
  }, [load]);

  // Under a user filter, a row with no verified subject matched by the
  // username/email recorded on it — a session from before the gateway reported
  // the subject (VSNP-1339). It is labelled, not hidden (VSNP-1437): hiding it
  // would make this list disagree with GET /sessions, /activity and
  // /audit/events for the same filter, and it would drop rejected attempts
  // that are part of the person's history.
  const isPreAttribution = (s: AccessSessionRow) => !!user && !s.userSub;
  const preAttributionCount = rows.filter(isPreAttribution).length;

  return (
    <div className="card" data-testid="sessions-table">
      <div className="mb-3 flex items-center gap-3">
        <h3 className="mr-auto text-sm font-semibold text-gray-900">
          {title} <span className="ml-1 text-xs font-normal text-gray-400" data-testid="sessions-total">{total} in range</span>
        </h3>
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(e) => setOpenOnly(e.target.checked)}
            data-testid="sessions-open-only"
          />
          Open only
        </label>
        <button type="button" className="btn-secondary px-2 py-1" onClick={() => void load()} title="Refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && <div className="mb-2 rounded border border-danger-200 bg-danger-50 px-3 py-2 text-xs text-danger-700">{error}</div>}

      {!loading && preAttributionCount > 0 && (
        <p className="mb-2 text-xs text-gray-500" data-testid="sessions-pre-attribution-note">
          {preAttributionCount} of these {rows.length} row{rows.length === 1 ? '' : 's'}{' '}
          {preAttributionCount === 1 ? 'was' : 'were'} recorded before identity attribution: they match
          &ldquo;{user}&rdquo; by the username or email recorded on the session, not by a verified IdP subject.
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3">Opened</th>
              <th className="py-2 pr-3">User</th>
              <th className="py-2 pr-3">Device</th>
              <th className="py-2 pr-3">Source</th>
              <th className="py-2 pr-3">Apps / hosts</th>
              {showNode && <th className="py-2 pr-3">Gateway</th>}
              <th className="py-2 pr-3 text-right">Flows</th>
              <th className="py-2 pr-3 text-right">↑ c2s</th>
              <th className="py-2 pr-3 text-right">↓ s2c</th>
              <th className="py-2 pr-3">Duration</th>
              <th className="py-2 pr-3">Close</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={showNode ? 11 : 10} className="py-6 text-center text-gray-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={showNode ? 11 : 10} className="py-6 text-center text-gray-400">No sessions in this window.</td></tr>
            ) : (
              rows.map((s) => (
                <tr
                  key={s.id}
                  className={`${onSelect ? 'cursor-pointer hover:bg-gray-50' : ''} ${selectedId === s.id ? 'bg-primary-50' : ''}`}
                  onClick={onSelect ? () => onSelect(s) : undefined}
                  data-testid="session-row"
                  data-session-id={s.sessionId}
                >
                  <td className="whitespace-nowrap py-1.5 pr-3 text-xs text-gray-600">
                    {s.openedAt ? s.openedAt.replace('T', ' ').slice(0, 19) : '—'}
                    {!s.closedAt && (
                      <span className="ml-1 rounded bg-success-100 px-1 py-0.5 text-[10px] font-medium text-success-700">open</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3">
                    <UserCell
                      user={s.user}
                      idpVerified={s.idpVerified}
                      userEmail={s.userEmail}
                      userSub={s.userSub}
                      preAttribution={isPreAttribution(s)}
                    />
                  </td>
                  <td className="py-1.5 pr-3">
                    <div className="text-xs font-medium text-gray-800">{s.deviceName || s.deviceCn || '—'}</div>
                    {s.deviceName && s.deviceCn && s.deviceName !== s.deviceCn && (
                      <div className="font-mono text-[10px] text-gray-400">{s.deviceCn}</div>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-[11px] text-gray-500">{s.srcIp ?? '—'}</td>
                  <td className="py-1.5 pr-3"><AppsCell apps={s.apps} /></td>
                  {showNode && <td className="py-1.5 pr-3 font-mono text-[11px] text-gray-500">{s.node}</td>}
                  <td className="py-1.5 pr-3 text-right text-xs text-gray-700">{s.flows}</td>
                  <td className="py-1.5 pr-3 text-right text-xs text-gray-700">{fmtBytes(s.bytesC2s)}</td>
                  <td className="py-1.5 pr-3 text-right text-xs text-gray-700">{fmtBytes(s.bytesS2c)}</td>
                  <td className="py-1.5 pr-3 text-xs text-gray-600">{fmtDuration(s.durationSeconds)}</td>
                  <td className="py-1.5 pr-3 text-xs text-gray-500">
                    {s.rejectReason ? (
                      <span className="rounded bg-danger-100 px-1 py-0.5 text-[10px] font-medium text-danger-700">
                        rejected: {s.rejectReason}
                      </span>
                    ) : (
                      s.closeReason ?? '—'
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * What a session touched, from `access_sessions.apps` (design §9b).
 *
 * Read from the COLUMN, not from a per-row audit query: this table renders up
 * to a hundred rows, and answering "which apps and hosts" from the log store
 * per row is exactly the cost the sessions table exists to avoid.
 *
 * Three shown, the rest counted. Hosts are preferred to app names in the label
 * because for breakout traffic the app is always `$internet` and the hostname
 * is the only thing that distinguishes one entry from another.
 */
const AppsCell = ({ apps }: { apps: SessionAppRollup[] | null }) => {
  if (!apps || apps.length === 0) {
    // NULL means the node never reported one — an older gateway build, or a
    // session that moved nothing. Saying "—" rather than "none" keeps it from
    // reading as a claim that the session reached nothing.
    return <span className="text-xs text-gray-300">—</span>;
  }
  const shown = apps.slice(0, 3);
  const rest = apps.length - shown.length;
  return (
    <span className="text-xs" data-testid="session-apps">
      {shown.map((a, i) => (
        <span key={`${a.app ?? ''}-${a.host ?? ''}-${i}`} className="mr-1 inline-block">
          {a.host ? (
            <span className="font-medium text-gray-800" data-testid="session-host">{a.host}</span>
          ) : (
            <span className="text-gray-700">{a.app || '—'}</span>
          )}
          {a.host && a.app && a.app !== '$internet' && (
            <span className="ml-0.5 text-[10px] text-gray-400">({a.app})</span>
          )}
          {i < shown.length - 1 && <span className="text-gray-300">,</span>}
        </span>
      ))}
      {rest > 0 && <span className="text-[10px] text-gray-400">+{rest}</span>}
    </span>
  );
};

export default SessionsTable;
